import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function CarRental() {
  const [rentals, setRentals] = useState([]);
  const [races, setRaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [form, setForm] = useState({ race_id: '', car_model: '', rental_date: '', return_date: '', store: '' });
  const [status, setStatus] = useState('');
  const [filterRace, setFilterRace] = useState('');
  const [search, setSearch] = useState('');
  const [passengersByRental, setPassengersByRental] = useState({});
  const [selectedUserByRental, setSelectedUserByRental] = useState({});
  const [editingRentalId, setEditingRentalId] = useState(null);
  const [editForm, setEditForm] = useState({ car_model: '', rental_date: '', return_date: '', store: '' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const racesP = fetch('/api/races', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
    const rentalsP = fetch('/api/car_rentals', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
    const usersP = fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
    const profileP = fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null);

    Promise.all([racesP, rentalsP, usersP, profileP]).then(([rData, rentData, uData, profileData]) => {
      setRaces(rData || []);
      setRentals(rentData || []);
      setUsers(uData || []);
      // Extract the user object from the API response
      const profile = profileData && profileData.user ? profileData.user : profileData;
      setCurrentUser(profile || null);
      console.log('Current user loaded:', profile); // DEBUG

      // load passengers for each rental
      (rentData || []).forEach(rental => {
        fetch(`/api/car_rentals/${rental.id}/passengers`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => res.json())
          .then(data => {
            console.log(`Passengers for rental ${rental.id}:`, data); // DEBUG
            setPassengersByRental(prev => ({ ...prev, [rental.id]: data || [] }));
          })
          .catch(() => setPassengersByRental(prev => ({ ...prev, [rental.id]: [] })));
      });
    }).catch(err => console.error('Failed to load car rental data', err));
  }, []);

  async function refreshRentals() {
    const token = localStorage.getItem('token');
    if (!token) return;
    const res = await fetch('/api/car_rentals', { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      setRentals(data || []);
      // refresh passengers for each rental
      data.forEach(rental => {
        fetch(`/api/car_rentals/${rental.id}/passengers`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => res.json())
          .then(d => setPassengersByRental(prev => ({ ...prev, [rental.id]: d || [] })))
          .catch(() => setPassengersByRental(prev => ({ ...prev, [rental.id]: [] })));
      });
    }
  }

  const toIsoLocal = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    // format to yyyy-MM-ddTHH:mm for datetime-local input
    return d.toISOString().slice(0, 16);
  };

  const canManageRental = (r) => currentUser && (currentUser.admin || String(currentUser.id) === String(r.user_id));

  function startEdit(r) {
    setEditingRentalId(r.id);
    setEditForm({
      car_model: r.car_model || '',
      rental_date: toIsoLocal(r.rental_date),
      return_date: toIsoLocal(r.return_date),
      store: r.store || ''
    });
  }

  function cancelEdit() {
    setEditingRentalId(null);
    setEditForm({ car_model: '', rental_date: '', return_date: '', store: '' });
  }

  async function submitEdit(rentalId) {
    const token = localStorage.getItem('token');
    if (!token) return;
    const payload = {
      car_model: editForm.car_model,
      rental_date: editForm.rental_date,
      return_date: editForm.return_date,
      store: editForm.store
    };
    const res = await fetch(`/api/edit_car_rentals/${rentalId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      await refreshRentals();
      cancelEdit();
    } else {
      setStatus(data.msg || 'Edit failed');
    }
  }

  async function deleteRental(rentalId) {
    const token = localStorage.getItem('token');
    if (!token) return;
    const confirmed = window.confirm('Remove this rental?');
    if (!confirmed) return;
    const res = await fetch(`/api/remove_car_rentals/${rentalId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      await refreshRentals();
    } else {
      setStatus(data.msg || 'Delete failed');
    }
  }

  async function joinRental(rentalId, userId = null) {
    const token = localStorage.getItem('token');
    if (!token) return;
    const body = (userId && currentUser && currentUser.admin) ? { user_id: userId } : {};
    const res = await fetch(`/api/car_rentals/${rentalId}/passengers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body)
    });
    if (res.ok) {
      // re-fetch passengers
      const p = await fetch(`/api/car_rentals/${rentalId}/passengers`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
      setPassengersByRental(prev => ({ ...prev, [rentalId]: p }));
      // clear selection for admin adds
      setSelectedUserByRental(prev => ({ ...prev, [rentalId]: '' }));
    }
  }

  async function leaveRental(rentalId, userId) {
    const token = localStorage.getItem('token');
    if (!token) return;
    const res = await fetch(`/api/car_rentals/${rentalId}/passengers/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      const p = await fetch(`/api/car_rentals/${rentalId}/passengers`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
      setPassengersByRental(prev => ({ ...prev, [rentalId]: p }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('Saving...');
    const token = localStorage.getItem('token');
    const payload = {
      race_id: form.race_id,
      car_model: form.car_model,
      rental_date: form.rental_date,
      return_date: form.return_date,
      store: form.store
    };

    try {
      const res = await fetch('/api/car_rentals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('Added');
        setForm({ race_id: '', car_model: '', rental_date: '', return_date: '', store: '' });
        await refreshRentals();
      } else {
        setStatus(data.msg || 'Failed');
      }
    } catch (err) {
      console.error(err);
      setStatus('Network error');
    }
  }

  const filtered = rentals.filter(r => {
    if (filterRace && String(r.race_id) !== String(filterRace)) return false;
    if (search) {
      const s = search.toLowerCase();
      return (r.car_model || '').toLowerCase().includes(s) || (r.store || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Car rentals</h1>
        <div className="text-sm text-gray-500">Manage rentals for upcoming races</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-3">Add rental</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <select value={form.race_id} onChange={e => setForm({...form, race_id: e.target.value})} className="w-full p-2 border rounded" required>
              <option value="">Select race</option>
              {races.map(r => <option key={r.id} value={r.id}>{r.name} — {r.location}</option>)}
            </select>

            <input value={form.car_model} onChange={e => setForm({...form, car_model: e.target.value})} placeholder="Car model" className="w-full p-2 border rounded" required />
            <input value={form.store} onChange={e => setForm({...form, store: e.target.value})} placeholder="Store" className="w-full p-2 border rounded" required />

            <label className="block text-sm text-gray-600">Rental date</label>
            <input type="datetime-local" value={form.rental_date} onChange={e => setForm({...form, rental_date: e.target.value})} className="w-full p-2 border rounded" required />
            <label className="block text-sm text-gray-600">Return date</label>
            <input type="datetime-local" value={form.return_date} onChange={e => setForm({...form, return_date: e.target.value})} className="w-full p-2 border rounded" required />

            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-gray-500">{status}</div>
              <button type="submit" className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Add rental</button>
            </div>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              <select value={filterRace} onChange={e => setFilterRace(e.target.value)} className="p-2 border rounded">
                <option value="">All races</option>
                {races.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search car model or store..." className="p-2 border rounded w-64" />
            </div>

          </div>

          <div className="space-y-3">
            {filtered.map(r => {
              const passengers = passengersByRental[r.id] || [];
              const passengerUsers = passengers.map(p => users.find(u => String(u.id) === String(p.user_id)) || { id: p.user_id, name: `User ${p.user_id}` });
              const amIPassenger = currentUser && passengerUsers.some(u => String(u.id) === String(currentUser.id));
              console.log(`Rental ${r.id}: amIPassenger=${amIPassenger}, currentUser.id=${currentUser?.id}, passengerUsers=${passengerUsers.map(u => u.id).join(',')}`); // DEBUG
              return (
              <div key={r.id} className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                <div>
                  <div className="text-lg font-medium">{r.car_model}</div>
                  <div className="text-sm text-gray-600">{r.store}</div>
                  <div className="text-xs text-gray-500 mt-1">{r.rental_date ? new Date(r.rental_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'} → {r.return_date ? new Date(r.return_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                  <div className="text-sm text-indigo-600 mt-1">Race: {races.find(rr => String(rr.id) === String(r.race_id))?.name || `Race ${r.race_id}`}</div>
                  <div className="text-sm text-gray-700 mt-2">Booked by: <Link to={`/users/${r.user_id}`} className="font-medium text-indigo-600 hover:underline">{(users.find(u=>String(u.id)===String(r.user_id))||{}).name || `User ${r.user_id}`}</Link></div>
                  <div className="text-sm text-gray-700 mt-2">Passengers: {passengerUsers.length === 0 ? (<span className="text-gray-500">None</span>) : (
                    <div className="flex flex-col gap-1">
                      {passengerUsers.map(u => (
                        <div key={u.id} className="flex items-center gap-2">
                          <Link to={`/users/${u.id}`} className="text-indigo-600 hover:underline">{u.name}</Link>
                          {(currentUser && (currentUser.admin || String(currentUser.id) === String(r.user_id))) && (
                            <button onClick={() => leaveRental(r.id, u.id)} className="text-xs px-2 py-0.5 bg-red-500 text-white rounded">Remove</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}</div>

                  {/* Admin: add arbitrary user as passenger */}
                  {currentUser && currentUser.admin && (
                    <div className="mt-2 flex items-center gap-2">
                      <select value={selectedUserByRental[r.id] || ''} onChange={e => setSelectedUserByRental(prev => ({ ...prev, [r.id]: e.target.value }))} className="p-1 border rounded">
                        <option value="">Add user...</option>
                        {users.filter(u => !passengerUsers.some(pu => String(pu.id) === String(u.id))).map(u => (
                          <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
                        ))}
                      </select>
                      <button disabled={!selectedUserByRental[r.id]} onClick={() => joinRental(r.id, selectedUserByRental[r.id])} className="px-2 py-1 bg-indigo-600 text-white rounded text-sm">Add passenger</button>
                    </div>
                  )}

                  {/* Edit form (inline) */}
                  {canManageRental(r) && editingRentalId === r.id && (
                    <div className="mt-3 p-3 border rounded bg-gray-50 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input value={editForm.car_model} onChange={e => setEditForm(f => ({ ...f, car_model: e.target.value }))} className="p-2 border rounded" placeholder="Car model" />
                        <input value={editForm.store} onChange={e => setEditForm(f => ({ ...f, store: e.target.value }))} className="p-2 border rounded" placeholder="Store" />
                        <input type="datetime-local" value={editForm.rental_date} onChange={e => setEditForm(f => ({ ...f, rental_date: e.target.value }))} className="p-2 border rounded" />
                        <input type="datetime-local" value={editForm.return_date} onChange={e => setEditForm(f => ({ ...f, return_date: e.target.value }))} className="p-2 border rounded" />
                      </div>
                      <div className="flex gap-2 justify-end text-sm">
                        <button onClick={cancelEdit} className="px-3 py-1 rounded border text-gray-700">Cancel</button>
                        <button onClick={() => submitEdit(r.id)} className="px-3 py-1 rounded bg-indigo-600 text-white">Save</button>
                      </div>
                    </div>
                  )}
                </div>
              <div className="flex flex-col items-end gap-2">
                  
                  {currentUser ? (
                    amIPassenger ? (
                      <button onClick={() => leaveRental(r.id, currentUser.id)} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Leave</button>
                    ) : (
                      <button onClick={() => joinRental(r.id)} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Join</button>
                    )
                  ) : (
                    <div className="text-xs text-gray-500">Loading...</div>
                  )}

                  {canManageRental(r) && (
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => startEdit(r)} className="px-3 py-1 bg-yellow-500 text-white rounded text-sm">Edit</button>
                      <button onClick={() => deleteRental(r.id)} className="px-3 py-1 bg-red-700 text-white rounded text-sm">Delete</button>
                    </div>
                  )}
                </div>
              </div>
            )})}
            {filtered.length === 0 && (
              <div className="bg-white rounded-lg shadow p-4 text-center text-gray-500">No rentals found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CarRental;